/**
 * CustomCarousel Component
 * ========================
 *
 * High-performance 3D carousel with smooth drag interactions.
 * Features circular positioning, drag-to-scroll, and responsive layout.
 *
 * Props:
 * - products: Array of product objects with { id, name, type, images: [...] }
 * - onProductClick: Callback when a product is clicked
 *
 * Extracted from ShopPage.js for reuse in collection pages.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ShoppingBag, X } from 'react-feather';
import OptimizedImage from './OptimizedImage';
import { getViewportDebugSnapshot, isDebugFlagEnabled, roundDebugValue } from '../../utils/motionDebug';
import './CustomCarousel.scss';

const MOBILE_BREAKPOINT = 768;
const STATIC_LAYOUT_GAP = 40;
const MIN_STATIC_DRAGGABLE_ITEMS = 7;
const CARD_ASPECT_RATIO = 9 / 16;
const LARGE_MOBILE_COLLECTION_THRESHOLD = 13;
const MOBILE_DEFAULT_MAX_RADIUS = 700;
const MOBILE_LARGE_COLLECTION_MAX_RADIUS = 1400;

const getDepthZIndex = (z, radius) => Math.round((z + radius * 2) * 10);

const getCircularTransformState = (relativeIndex, totalItems, radius) => {
  const angle = (relativeIndex * 360) / totalItems;
  const radian = (angle * Math.PI) / 180;
  const x = Math.sin(radian) * radius;
  const z = Math.cos(radian) * radius - radius;
  const distanceFromCenter = Math.abs(z) / radius;
  const distanceSquared = distanceFromCenter * distanceFromCenter;
  const scale = Math.max(0.6, 1 - distanceSquared * 0.35);
  const opacity = Math.max(0.25, 1 - distanceSquared * 0.65);
  const zIndex = getDepthZIndex(z, radius);

  return {
    angle,
    x,
    z,
    scale,
    opacity,
    zIndex
  };
};

const getCarouselMetrics = (viewportWidth, viewportHeight, totalItems) => {
  const isMobile = viewportWidth < MOBILE_BREAKPOINT;
  const maxHeight = Math.min(viewportHeight * 0.8, viewportHeight - 120);
  const maxWidthFromHeight = maxHeight * CARD_ASPECT_RATIO;
  const itemWidth = isMobile
    ? Math.min(viewportWidth * 0.85, maxWidthFromHeight)
    : Math.min(viewportWidth * 0.3, maxWidthFromHeight, 600);
  const itemHeight = itemWidth / CARD_ASPECT_RATIO;
  const desiredGap = isMobile ? 40 : 80;
  const staticSpacing = itemWidth + STATIC_LAYOUT_GAP;
  const maxItemsThatFit = Math.floor((viewportWidth * 0.9) / staticSpacing);
  const allItemsFit = totalItems <= maxItemsThatFit;
  const circumference = totalItems * (itemWidth + desiredGap);
  const calculatedRadius = Math.max(300, circumference / (2 * Math.PI));
  const mobileRadiusCap = totalItems >= LARGE_MOBILE_COLLECTION_THRESHOLD
    ? MOBILE_LARGE_COLLECTION_MAX_RADIUS
    : MOBILE_DEFAULT_MAX_RADIUS;
  const radius = isMobile ? Math.min(calculatedRadius, mobileRadiusCap) : Math.min(calculatedRadius, 1800);
  const shouldDisableDragging = allItemsFit && totalItems <= MIN_STATIC_DRAGGABLE_ITEMS;

  return {
    isMobile,
    itemWidth,
    itemHeight,
    desiredGap,
    staticSpacing,
    maxItemsThatFit,
    allItemsFit,
    radius,
    shouldDisableDragging
  };
};

const CustomCarousel = ({
  products,
  onProductClick,
  navigateOnAnySlide = false,
  collectionInfo = null,
  debugMode = false,
  debugLabel = null,
  onDebugStateChange = null,
  isPriorityActive = true
}) => {
  // ALL HOOKS MUST BE DECLARED FIRST - unconditionally
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);
  const [snapTarget, setSnapTarget] = useState(0);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [lastPosition, setLastPosition] = useState(0);
  const [lastTime, setLastTime] = useState(0);
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [totalDragDistance, setTotalDragDistance] = useState(0);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const gestureDirectionRef = useRef(null); // null | 'horizontal' | 'vertical'

  // Animation states for smooth enter/exit
  const [animatingProducts, setAnimatingProducts] = useState([]);
  const [exitingProducts, setExitingProducts] = useState([]);

  // Tilt effect state - track mouse position relative to each card
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cardRefs = useRef({});
  const slideWrapperRefs = useRef({}); // Store refs to slide wrapper elements for direct DOM manipulation during snap

  const carouselRef = useRef();
  const animationFrameRef = useRef();
  const dragAnimationFrameRef = useRef(); // For throttling drag updates to once per frame
  const pendingDragOffsetRef = useRef(null); // Store pending drag offset to avoid state thrashing
  const prevProductsRef = useRef(products);
  const gestureSessionRef = useRef(0); // Incremented per drag to correlate logs
  const gestureStateRef = useRef({
    currentIndex: 0,
    isDragging: false,
    isSnapping: false,
    snapTarget: 0
  });
  const snapAnimatedIndexRef = useRef(0); // Tracks current animated index during snap for interruption
  const dragStartPositionRef = useRef({ x: 0, y: 0 }); // For tap detection in slide pointerUp
  const tapActionHandledRef = useRef(false);
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [flippedIndex, setFlippedIndex] = useState(null); // Index of card showing back (garment info)
  const debugEnabled = useMemo(
    () => debugMode || isDebugFlagEnabled('debugMotion', 'debugCarousel'),
    [debugMode]
  );
  const shouldVerboseLog = debugEnabled && process.env.NODE_ENV === 'development';
  const resolvedDebugLabel = debugLabel || collectionInfo?.projectId || collectionInfo?.projectName || 'carousel';

  // Handle smooth enter/exit animations when products change
  useEffect(() => {
    const prevProducts = prevProductsRef.current;
    const currentProducts = products;

    // If products changed, trigger animations
    if (prevProducts.length !== currentProducts.length ||
        prevProducts.some((p, i) => p.id !== currentProducts[i]?.id)) {


      // Find products that are exiting
      const exiting = prevProducts.filter(prev =>
        !currentProducts.some(curr => curr.id === prev.id)
      );

      // Find products that are entering
      const entering = currentProducts.filter(curr =>
        !prevProducts.some(prev => prev.id === curr.id)
      );

      // Set exiting products for fade-out animation
      if (exiting.length > 0) {
        setExitingProducts(exiting);
      }

      // Set entering products for fade-in animation
      if (entering.length > 0) {
        setAnimatingProducts(entering);

        // Trigger enter animation after a small delay for exiting items
        setTimeout(() => {
          setAnimatingProducts([]);
        }, 200);
      }

      // Clean up after animation duration
      const cleanupTimer = setTimeout(() => {
        setExitingProducts([]);
        setAnimatingProducts([]);
      }, 600); // 600ms total animation time

      return () => clearTimeout(cleanupTimer);
    }

    prevProductsRef.current = products;
  }, [products]);

  // Reset flip when user navigates to another slide
  useEffect(() => {
    setFlippedIndex(null);
  }, [currentIndex]);

  // Handle viewport resize to update carousel sizing
  useEffect(() => {
    const handleResize = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Combine collection info card with products if provided; sort products by order when present
  const allItems = useMemo(() => {
    const sortedProducts = [...products].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    return collectionInfo && (collectionInfo.introText || collectionInfo.aboutImageSrc)
      ? [{ id: 'collection-info', isCollectionInfo: true, ...collectionInfo }, ...sortedProducts]
      : sortedProducts;
  }, [
    products,
    collectionInfo?.projectId,
    collectionInfo?.projectName,
    collectionInfo?.season,
    collectionInfo?.introText,
    collectionInfo?.themeColor,
    collectionInfo?.aboutImageSrc
  ]);
  const itemCount = allItems.length;
  const viewportWidth = viewportSize.width;
  const viewportHeight = viewportSize.height;
  const tiltEnabled = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    return supportsHover && viewportWidth >= MOBILE_BREAKPOINT;
  }, [viewportWidth]);
  const carouselMetrics = useMemo(
    () => getCarouselMetrics(viewportWidth, viewportHeight, itemCount),
    [viewportWidth, viewportHeight, itemCount]
  );
  const {
    itemWidth,
    itemHeight,
    staticSpacing,
    allItemsFit,
    radius,
    shouldDisableDragging: dragDisabled
  } = carouselMetrics;
  const shouldSuspendHeavyRender = carouselMetrics.isMobile && !isPriorityActive;
  const shouldPromoteLayers = !shouldSuspendHeavyRender && (isDragging || isSnapping);

  useEffect(() => {
    if (tiltEnabled) return;

    setHoveredCardIndex(null);
    setMousePosition({ x: 0, y: 0 });
  }, [tiltEnabled]);

  useEffect(() => {
    if (!shouldSuspendHeavyRender) return;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (dragAnimationFrameRef.current) {
      cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = null;
    }

    pendingDragOffsetRef.current = null;
    gestureDirectionRef.current = null;
    tapActionHandledRef.current = false;
    setIsDragging(false);
    setIsSnapping(false);
    setDragOffset(0);
    setHoveredCardIndex(null);
    setMousePosition({ x: 0, y: 0 });
  }, [shouldSuspendHeavyRender]);

  const describeFrontCard = useCallback((indexValue) => {
    const total = allItems.length;
    if (!total) {
      return { index: null, id: null, label: 'n/a', rawIndex: indexValue };
    }

    const sourceIndex = typeof indexValue === 'number' ? indexValue : gestureStateRef.current.currentIndex;
    const normalized = ((Math.round(sourceIndex) % total) + total) % total;
    const item = allItems[normalized];
    const label = item?.name || item?.projectName || item?.id || `slide-${normalized}`;

    return {
      index: normalized,
      id: item?.id ?? null,
      label,
      rawIndex: sourceIndex
    };
  }, [allItems]);

  const logGesture = useCallback((phase, meta = {}) => {
    if (!debugEnabled) return;

    const formatIndex = (value) => (typeof value === 'number' ? Number(value.toFixed(3)) : value);
    const { currentIndex: latestIndex, isDragging: latestDragging, isSnapping: latestSnapping, snapTarget: latestSnapTarget } = gestureStateRef.current;
    const indexForLog = meta.indexOverride ?? latestIndex;

    console.log('[Carousel][gesture]', {
      carousel: resolvedDebugLabel,
      phase,
      gestureId: gestureSessionRef.current,
      dragging: latestDragging,
      snapping: latestSnapping,
      currentIndex: formatIndex(latestIndex),
      snapTarget: latestSnapTarget,
      frontCard: describeFrontCard(indexForLog),
      ...meta
    });
  }, [debugEnabled, describeFrontCard, resolvedDebugLabel]);

  useEffect(() => {
    if (!debugEnabled) return;

    console.log('[Carousel][metrics]', {
      carousel: resolvedDebugLabel,
      itemCount,
      metrics: {
        itemWidth: roundDebugValue(itemWidth, 2),
        itemHeight: roundDebugValue(itemHeight, 2),
        radius: roundDebugValue(radius, 2),
        staticSpacing: roundDebugValue(staticSpacing, 2),
        allItemsFit,
        dragDisabled
      },
      viewport: getViewportDebugSnapshot()
    });
  }, [allItemsFit, debugEnabled, dragDisabled, itemCount, itemHeight, itemWidth, radius, resolvedDebugLabel, staticSpacing]);

  useEffect(() => {
    if (!onDebugStateChange) return;

    onDebugStateChange({
      carousel: resolvedDebugLabel,
      currentIndex: roundDebugValue(currentIndex),
      dragOffset: roundDebugValue(dragOffset),
      isDragging,
      isSnapping,
      snapTarget,
      totalDragDistance: roundDebugValue(totalDragDistance, 2),
      frontCard: describeFrontCard(),
      metrics: {
        itemWidth: roundDebugValue(itemWidth, 2),
        itemHeight: roundDebugValue(itemHeight, 2),
        radius: roundDebugValue(radius, 2),
        allItemsFit,
        dragDisabled
      },
      viewport: getViewportDebugSnapshot()
    });
  }, [
    allItemsFit,
    currentIndex,
    describeFrontCard,
    dragDisabled,
    dragOffset,
    isDragging,
    isSnapping,
    itemHeight,
    itemWidth,
    onDebugStateChange,
    radius,
    resolvedDebugLabel,
    snapTarget,
    totalDragDistance
  ]);

  const formatDisplayLabel = useCallback((value = '') => (
    String(value)
      .replace(/_/g, ' ')
      .replace(/\s*\+\s*/g, ', ')
      .replace(/\s*,\s*/g, ', ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase()
  ), []);

  const formatGarmentLabel = useCallback((garment) => (
    formatDisplayLabel([garment?.name, garment?.type].filter(Boolean).join(' '))
  ), [formatDisplayLabel]);

  const formatItemDisplayTitle = useCallback((item) => {
    if (item?.garmentData?.length) {
      return item.garmentData.map((garment) => formatGarmentLabel(garment)).join(', ');
    }

    return formatDisplayLabel([item?.name, item?.type].filter(Boolean).join(' '));
  }, [formatDisplayLabel, formatGarmentLabel]);

  const formatMaterialsLine = useCallback((materials) => {
    if (!materials) return '';

    const segments = (Array.isArray(materials) ? materials : [materials])
      .flatMap((value) => String(value).split(','))
      .map((value) => value.trim())
      .filter(Boolean);

    return segments.map((segment) => {
      const upperSegment = segment.toUpperCase();
      return /^[0-9]/.test(upperSegment) ? upperSegment : `100% ${upperSegment}`;
    }).join(', ');
  }, []);

  useEffect(() => {
    gestureStateRef.current = {
      currentIndex,
      isDragging,
      isSnapping,
      snapTarget
    };
  }, [currentIndex, isDragging, isSnapping, snapTarget]);

  // Calculate item position and scale based on index and offset
  const getItemTransform = useCallback((index, offset = 0) => {
    const totalItems = allItems.length;

    // Simplified logic: static centered layout if all items fit, otherwise 3D carousel
    if (allItemsFit) {
      // Static centered layout - all items same size and visible
      // Apply offset to allow dragging even in static layout
      const startOffset = -(totalItems - 1) * staticSpacing / 2;
      const x = startOffset + (index + offset) * staticSpacing;
      const z = 0;

      // All items clickable and same size
      const scale = 1;
      const opacity = 1;

      return {
        transform: `translate3d(${x}px, 0, ${z}px) scale(${scale})`,
        opacity,
        zIndex: 90,
        isCenter: true, // All items are "center" in static layout
        angle: 0,
        staticLayout: true
      };
    }

    // 3D circular carousel for when items don't all fit
    const {
      angle,
      x,
      z,
      scale,
      opacity,
      zIndex
    } = getCircularTransformState(index - currentIndex + offset, totalItems, radius);

    // Center detection for click handling - more forgiving threshold during wrapping
    const baseThreshold = (360 / totalItems / 2);
    const expandedThreshold = baseThreshold * 1.2; // 20% more forgiving

    // Special case: during wrap transitions, also check if this is close to being the target slide
    const normalizedCurrentIndex = ((currentIndex % totalItems) + totalItems) % totalItems;
    const isNearTargetDuringWrap = Math.abs(index - Math.round(normalizedCurrentIndex)) < 0.1;

    const isCenter = Math.abs(angle) < expandedThreshold || isNearTargetDuringWrap;

    return {
      transform: `translate3d(${x}px, 0, ${z}px) scale(${scale})`,
      opacity,
      zIndex,
      isCenter,
      angle,
      staticLayout: false
    };
  }, [allItems.length, allItemsFit, currentIndex, radius, staticSpacing]);

  // Calculate tilt transform based on mouse position
  const getTiltTransform = useCallback((cardIndex) => {
    // Only apply tilt when hovering and not dragging
    if (!tiltEnabled || hoveredCardIndex !== cardIndex || isDragging) {
      return { rotateX: 0, rotateY: 0, scale: 1, lightX: 0, lightY: 0 };
    }

    const cardRef = cardRefs.current[cardIndex];
    if (!cardRef) {
      return { rotateX: 0, rotateY: 0, scale: 1, lightX: 0, lightY: 0 };
    }

    const rect = cardRef.getBoundingClientRect();
    const cardCenterX = rect.left + rect.width / 2;
    const cardCenterY = rect.top + rect.height / 2;
    const cardWidth = rect.width;
    const cardHeight = rect.height;

    // Calculate relative position (-1 to 1)
    const relativeX = (mousePosition.x - cardCenterX) / (cardWidth / 2);
    const relativeY = (mousePosition.y - cardCenterY) / (cardHeight / 2);

    // Clamp to -1 to 1 range
    const clampedX = Math.max(-1, Math.min(1, relativeX));
    const clampedY = Math.max(-1, Math.min(1, relativeY));

    // Calculate tilt angles (max 6 degrees for subtlety)
    const maxTilt = 6;
    const rotateY = clampedX * maxTilt; // Horizontal mouse movement rotates around Y axis
    const rotateX = -clampedY * maxTilt; // Vertical mouse movement rotates around X axis (inverted)

    // Subtle scale on hover
    const scale = 1.01;

    // Light position for gradient effect (0-100% for CSS)
    const lightX = ((clampedX + 1) / 2) * 100;
    const lightY = ((clampedY + 1) / 2) * 100;

    return { rotateX, rotateY, scale, lightX, lightY };
  }, [hoveredCardIndex, isDragging, mousePosition, tiltEnabled]);

  // Mouse event handlers for tilt effect
  const handleCardMouseEnter = useCallback((index, e) => {
    if (!tiltEnabled || isDragging) return;
    setHoveredCardIndex(index);
    setMousePosition({ x: e.clientX, y: e.clientY });
  }, [isDragging, tiltEnabled]);

  const handleCardMouseMove = useCallback((index, e) => {
    if (!tiltEnabled || isDragging) return;
    // Update hovered index if not set (handles race condition)
    if (hoveredCardIndex !== index) {
      setHoveredCardIndex(index);
    }
    setMousePosition({ x: e.clientX, y: e.clientY });
  }, [isDragging, hoveredCardIndex, tiltEnabled]);

  const handleCardMouseLeave = useCallback((e) => {
    if (!tiltEnabled || isDragging) return;

    // Check if we're actually leaving the card or just entering a child element
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && (relatedTarget instanceof Node) && e.currentTarget.contains(relatedTarget)) {
      return;
    }

    setHoveredCardIndex(null);
    setMousePosition({ x: 0, y: 0 });
  }, [isDragging, tiltEnabled]);

  // Helper to fully reset drag state (used when vertical scrolling is detected)
  const resetDragState = useCallback(() => {
    // Cancel any pending drag animation frame
    if (dragAnimationFrameRef.current) {
      cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = null;
    }
    pendingDragOffsetRef.current = null;
    setIsDragging(false);
    setDragOffset(0);
    setTotalDragDistance(0);
    setHoveredCardIndex(null);
    setMousePosition({ x: 0, y: 0 });
    gestureDirectionRef.current = null;
  }, []);

  // Mouse event handlers for carousel dragging
  // Throttled to once per frame using requestAnimationFrame to prevent state thrashing
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    // Calculate movement from start position
    const deltaY = Math.abs(e.clientY - dragStartPosition.y);
    const deltaX = Math.abs(e.clientX - dragStartPosition.x);

    // If the user is scrolling vertically, abort dragging and allow page to scroll
    if (deltaY > deltaX * 1.2 && deltaY > 18) {
      if (shouldVerboseLog) {
        console.log('[Carousel][mouseMove] vertical-detected -> resetDragState', { deltaX, deltaY });
      }
      logGesture('drag-reset', {
        pointer: 'mouse',
        reason: 'vertical-detected',
        delta: { x: deltaX, y: deltaY }
      });
      resetDragState();
      return;
    }

    const currentTime = Date.now();
    const deltaTime = currentTime - lastTime;
    const horizontalDeltaX = e.clientX - lastPosition;

    // Calculate velocity (pixels per ms) for adaptive sensitivity
    let velocity = 0;
    if (deltaTime > 0) {
      velocity = horizontalDeltaX / deltaTime;
      // Clamp velocity for stability
      velocity = Math.max(-20, Math.min(20, velocity * 3));
    }

    // Adaptive drag sensitivity: reduce sensitivity at high velocities to prevent overshooting
    // Base sensitivity: 200px = 1 carousel unit
    // At high velocity (>5 px/ms), increase divisor to reduce sensitivity
    const baseSensitivity = 200;
    const velocityThreshold = 5; // px/ms
    const sensitivityMultiplier = Math.abs(velocity) > velocityThreshold
      ? 1 + (Math.abs(velocity) - velocityThreshold) * 0.1 // Gradually reduce sensitivity
      : 1;
    const sensitivity = baseSensitivity * sensitivityMultiplier;

    const offset = (dragStart - e.clientX) / sensitivity;

    // Store latest offset in ref (no React state update yet)
    pendingDragOffsetRef.current = {
      offset,
      clientX: e.clientX,
      time: currentTime,
      velocity
    };

    // Schedule state update for next frame (throttle to once per frame)
    if (!dragAnimationFrameRef.current) {
      dragAnimationFrameRef.current = requestAnimationFrame(() => {
        dragAnimationFrameRef.current = null;

        // Only update if we still have a pending value and are still dragging
        if (pendingDragOffsetRef.current && isDragging) {
          const pending = pendingDragOffsetRef.current;

          if (shouldVerboseLog && Math.abs(pending.velocity) > 3) {
            // Log fast drags to help debug
            console.log('[Carousel][mouseMove] fast-drag (throttled)', {
              velocity: pending.velocity.toFixed(2),
              sensitivity: sensitivity.toFixed(0),
              offset: pending.offset.toFixed(3),
              deltaTime: pending.time - lastTime
            });
          }

          setDragOffset(pending.offset);
          setLastPosition(pending.clientX);
          setLastTime(pending.time);
        }
      });
    }
  }, [isDragging, dragStart, dragStartPosition, lastPosition, lastTime, logGesture, resetDragState, shouldVerboseLog]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging) return;

    // Cancel any pending drag animation frame
    if (dragAnimationFrameRef.current) {
      cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = null;
    }

    setIsDragging(false);

    // Flush any pending drag offset before calculating final position
    let finalDragOffset = dragOffset;
    if (pendingDragOffsetRef.current) {
      finalDragOffset = pendingDragOffsetRef.current.offset;
      setDragOffset(finalDragOffset);
      setLastPosition(pendingDragOffsetRef.current.clientX);
      setLastTime(pendingDragOffsetRef.current.time);
      pendingDragOffsetRef.current = null;
    }

    // Calculate total drag distance to detect if this was a drag or click
    const endX = e?.clientX || 0;
    const endY = e?.clientY || 0;
    const dragDistanceX = Math.abs(endX - dragStartPosition.x);
    const dragDistanceY = Math.abs(endY - dragStartPosition.y);
    const totalDistance = Math.sqrt(dragDistanceX * dragDistanceX + dragDistanceY * dragDistanceY);
    setTotalDragDistance(totalDistance);

    if (tapActionHandledRef.current) {
      tapActionHandledRef.current = false;
      resetDragState();
      return;
    }

    // If this was primarily vertical movement, don't update carousel position
    if (dragDistanceY > dragDistanceX && dragDistanceY > 10) {
      // User was scrolling vertically, not dragging carousel
      if (shouldVerboseLog) {
        console.log('[Carousel][mouseUp] vertical-move -> abort drag', { dragDistanceX, dragDistanceY });
      }
      logGesture('drag-cancel', {
        pointer: 'mouse',
        reason: 'vertical-move',
        dragDistance: { x: dragDistanceX, y: dragDistanceY }
      });
      resetDragState();
      return;
    }

    // Calculate the final position after drag (use flushed offset)
    let finalPosition = currentIndex + finalDragOffset;
    const totalItems = allItems.length;

    // Determine target based on drag direction and magnitude
    let targetIndex;
    const dragMagnitude = Math.abs(finalDragOffset);

    if (dragMagnitude < 0.1) {
      targetIndex = Math.round(finalPosition);
    } else {
      if (finalDragOffset > 0) {
        targetIndex = Math.ceil(finalPosition);
      } else {
        targetIndex = Math.floor(finalPosition);
      }
    }

    // All carousels use 3D wrapping logic
    // Don't normalize the target - let the animation system handle it
    if (shouldVerboseLog) {
      console.log('[Carousel][mouseUp] snap-request', { finalPosition, targetIndex, dragMagnitude, totalItems });
    }
    logGesture('drag-end', {
      pointer: 'mouse',
      finalPosition,
      targetIndex,
      dragMagnitude,
      totalDragDistance: totalDistance,
      indexOverride: finalPosition
    });
    setCurrentIndex(finalPosition);
    setSnapTarget(targetIndex);
    setDragOffset(0);
    setIsSnapping(true);
  }, [isDragging, dragOffset, currentIndex, allItems.length, dragStartPosition, logGesture, resetDragState, shouldVerboseLog]);

  // Touch event handlers
  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - dragStartPosition.y);
    const deltaX = Math.abs(touch.clientX - dragStartPosition.x);

    // Decide once whether this gesture is horizontal or vertical
    // Use higher threshold and more conservative vertical detection to avoid false positives
    if (gestureDirectionRef.current === null) {
      const threshold = 20; // Increased from 10px - require more movement before deciding
      const isGestureLargeEnough = deltaX > threshold || deltaY > threshold;
      if (isGestureLargeEnough) {
        // More conservative: require horizontal to be 1.5x vertical (was 1.2x)
        // AND require at least 20px horizontal movement to decide horizontal
        // This prevents false vertical detection when starting a horizontal swipe
        const isHorizontal = deltaX >= deltaY * 1.5 && deltaX >= 20;
        const isVertical = deltaY >= deltaX * 1.5 && deltaY >= 25; // Require more vertical to be sure

        if (isHorizontal) {
          gestureDirectionRef.current = 'horizontal';
          // Immediately preventDefault once we've decided it's horizontal to stop vertical scrolling
          if (e.cancelable) {
            e.preventDefault();
            if (shouldVerboseLog) {
              console.log('[Carousel][touchMove] preventDefault (horizontal decided)', { deltaX, deltaY });
            }
          }
        } else if (isVertical) {
          gestureDirectionRef.current = 'vertical';
        } else {
          // Ambiguous - wait for more movement, don't decide yet
          // This allows horizontal swipes that start with small vertical component
          return;
        }

        if (shouldVerboseLog) {
          console.log('[Carousel][touchMove] decide-direction', { deltaX, deltaY, decided: gestureDirectionRef.current });
        }
      } else {
        // Not enough movement yet - wait
        return;
      }
    }

    // Only reset if we're very confident it's vertical (more movement required)
    if (gestureDirectionRef.current === 'vertical') {
      // Require significant vertical movement before resetting (prevents false positives)
      // Also check if horizontal has become dominant - allow re-evaluation
      if (deltaY >= 35 && deltaY > deltaX * 1.8) {
        if (shouldVerboseLog) {
          console.log('[Carousel][touchMove] vertical-detected -> resetDragState', { deltaX, deltaY });
        }
        logGesture('drag-reset', {
          pointer: 'touch',
          reason: 'vertical-detected',
          delta: { x: deltaX, y: deltaY }
        });
        resetDragState(); // give control back to native scroll
        return;
      } else if (deltaX > deltaY * 1.5 && deltaX >= 30) {
        // Horizontal has become dominant - re-evaluate direction
        if (shouldVerboseLog) {
          console.log('[Carousel][touchMove] re-evaluating: horizontal became dominant', { deltaX, deltaY });
        }
        gestureDirectionRef.current = 'horizontal';
        // Prevent default now that we've switched to horizontal
        if (e.cancelable) {
          e.preventDefault();
          if (shouldVerboseLog) {
            console.log('[Carousel][touchMove] preventDefault (re-evaluated to horizontal)', { deltaX, deltaY });
          }
        }
        // Continue processing as horizontal
      } else {
        // Not enough movement in either direction yet - wait, don't process
        return;
      }
    }

    // If we've confirmed horizontal, ensure preventDefault is called (may have been called earlier)
    // This is a safety check in case we missed it during direction decision
    const shouldPreventDefault = gestureDirectionRef.current === 'horizontal' && e.cancelable;

    const currentTime = Date.now();
    const deltaTime = currentTime - lastTime;
    const deltaXRaw = touch.clientX - lastPosition;

    // Calculate velocity (pixels per ms) for adaptive sensitivity
    let velocity = 0;
    if (deltaTime > 0) {
      velocity = deltaXRaw / deltaTime;
      // Clamp velocity for stability
      velocity = Math.max(-20, Math.min(20, velocity * 3));
    }

    // Adaptive drag sensitivity: reduce sensitivity at high velocities to prevent overshooting
    // Base sensitivity: 200px = 1 carousel unit
    // At high velocity (>5 px/ms), increase divisor to reduce sensitivity
    const baseSensitivity = 200;
    const velocityThreshold = 5; // px/ms
    const sensitivityMultiplier = Math.abs(velocity) > velocityThreshold
      ? 1 + (Math.abs(velocity) - velocityThreshold) * 0.1 // Gradually reduce sensitivity
      : 1;
    const sensitivity = baseSensitivity * sensitivityMultiplier;

    const offset = (dragStart - touch.clientX) / sensitivity;

    // Store latest offset in ref (no React state update yet)
    pendingDragOffsetRef.current = {
      offset,
      clientX: touch.clientX,
      time: currentTime,
      velocity
    };

    // Schedule state update for next frame (throttle to once per frame)
    if (!dragAnimationFrameRef.current) {
      dragAnimationFrameRef.current = requestAnimationFrame(() => {
        dragAnimationFrameRef.current = null;

        // Only update if we still have a pending value and are still dragging
        if (pendingDragOffsetRef.current && isDragging) {
          const pending = pendingDragOffsetRef.current;

          if (shouldVerboseLog && Math.abs(pending.velocity) > 3) {
            // Log fast drags to help debug
            console.log('[Carousel][touchMove] fast-drag (throttled)', {
              velocity: pending.velocity.toFixed(2),
              sensitivity: sensitivity.toFixed(0),
              offset: pending.offset.toFixed(3),
              deltaTime: pending.time - lastTime
            });
          }

          setDragOffset(pending.offset);
          setLastPosition(pending.clientX);
          setLastTime(pending.time);
        }
      });
    }

    // Safety check: ensure preventDefault is called for horizontal drags
    // (We already call it when deciding direction, but this ensures we don't miss it)
    if (shouldPreventDefault && e.cancelable) {
      e.preventDefault();
    }
  }, [isDragging, dragStart, dragStartPosition, lastPosition, lastTime, logGesture, resetDragState, shouldVerboseLog]);

  const handleTouchEnd = useCallback((e) => {
    if (!isDragging) return;

    // Cancel any pending drag animation frame
    if (dragAnimationFrameRef.current) {
      cancelAnimationFrame(dragAnimationFrameRef.current);
      dragAnimationFrameRef.current = null;
    }

    setIsDragging(false);

    // Flush any pending drag offset before calculating final position
    let finalDragOffset = dragOffset;
    if (pendingDragOffsetRef.current) {
      finalDragOffset = pendingDragOffsetRef.current.offset;
      setDragOffset(finalDragOffset);
      setLastPosition(pendingDragOffsetRef.current.clientX);
      setLastTime(pendingDragOffsetRef.current.time);
      pendingDragOffsetRef.current = null;
    }

    // Calculate total drag distance for touch
    const touch = e?.changedTouches?.[0];
    const endX = touch?.clientX || 0;
    const endY = touch?.clientY || 0;
    const dragDistanceX = endX - dragStartPosition.x;
    const dragDistanceY = Math.abs(endY - dragStartPosition.y);
    const absDragDistanceX = Math.abs(dragDistanceX);
    const totalDistance = Math.sqrt(absDragDistanceX * absDragDistanceX + dragDistanceY * dragDistanceY);
    setTotalDragDistance(totalDistance);

    if (tapActionHandledRef.current) {
      tapActionHandledRef.current = false;
      resetDragState();
      return;
    }

    // If this was primarily vertical movement, don't update carousel position
    if (dragDistanceY > absDragDistanceX && dragDistanceY > 22) {
      // User was scrolling vertically, not dragging carousel
      if (shouldVerboseLog) {
        console.log('[Carousel][touchEnd] vertical-move -> abort drag', { absDragDistanceX, dragDistanceY });
      }
      logGesture('drag-cancel', {
        pointer: 'touch',
        reason: 'vertical-move',
        dragDistance: { x: absDragDistanceX, y: dragDistanceY }
      });
      resetDragState();
      return;
    }

    // Calculate swipe velocity (time since touch start)
    const touchDuration = Date.now() - touchStartTime;
    const swipeVelocity = absDragDistanceX / Math.max(touchDuration, 1); // pixels per ms

    // Detect swipe gesture: horizontal movement > 50px, velocity > 0.3 px/ms, and more horizontal than vertical
    const isSwipe = absDragDistanceX > 50 &&
                    swipeVelocity > 0.3 &&
                    absDragDistanceX > dragDistanceY * 1.5;

    const totalItems = allItems.length;

    // If it's a swipe, navigate to next/previous item directly
    if (isSwipe && totalItems > 1) {
      let targetIndex;
      const baseIndex = Math.round(currentIndex); // ensure swipe snaps to whole slide
      const visualStartIndex = currentIndex + finalDragOffset;
      if (dragDistanceX > 0) {
        // Swiped right - go to previous item (allow wrapping)
        targetIndex = baseIndex - 1;
      } else {
        // Swiped left - go to next item (allow wrapping)
        targetIndex = baseIndex + 1;
      }

      if (shouldVerboseLog) {
        console.log('[Carousel][touchEnd] swipe-detected', { absDragDistanceX, dragDistanceY, swipeVelocity, targetIndex });
      }
      logGesture('drag-end', {
        pointer: 'touch',
        finalPosition: currentIndex,
        targetIndex,
        dragMagnitude: absDragDistanceX,
        totalDragDistance: totalDistance,
        swipeVelocity,
        detectedSwipe: true,
        baseIndex,
        visualStartIndex: roundDebugValue(visualStartIndex)
      });
      // Keep the exact dragged visual position so release does not "snap back" before animating.
      setCurrentIndex(visualStartIndex);
      setSnapTarget(targetIndex);
      setIsSnapping(true);
      setDragOffset(0);
      return;
    }

    // Otherwise, use existing drag logic for carousel rotation (use flushed offset)
    const finalPosition = currentIndex + finalDragOffset;
    let targetIndex;
    const dragMagnitude = Math.abs(finalDragOffset);

    if (dragMagnitude < 0.1) {
      targetIndex = Math.round(finalPosition);
    } else {
      if (finalDragOffset > 0) {
        targetIndex = Math.ceil(finalPosition);
      } else {
        targetIndex = Math.floor(finalPosition);
      }
    }

    // All carousels use 3D wrapping logic
    if (shouldVerboseLog) {
      console.log('[Carousel][touchEnd] snap-request', { finalPosition, targetIndex, dragMagnitude });
    }
    logGesture('drag-end', {
      pointer: 'touch',
      finalPosition,
      targetIndex,
      dragMagnitude,
      totalDragDistance: totalDistance,
      swipeVelocity,
      detectedSwipe: false,
      indexOverride: finalPosition
    });
    setCurrentIndex(finalPosition);
    setSnapTarget(targetIndex);
    setDragOffset(0);
    setIsSnapping(true);
  }, [isDragging, dragOffset, currentIndex, allItems.length, dragStartPosition, logGesture, resetDragState, shouldVerboseLog, touchStartTime]);

  // Handle snapping animation to target slide
  // Read the starting index from the gesture ref so the effect doesn't restart on unrelated renders.
  useEffect(() => {
    if (!isSnapping) return;

    const startIndex = gestureStateRef.current.currentIndex;
    let targetIndex = snapTarget;
    const totalItems = itemCount;

    // Check if we're already at the target (prevent infinite loops)
    const difference = Math.abs(startIndex - targetIndex);
    if (difference < 0.01) {
      setIsSnapping(false);
      setIsDragging(false);
      return;
    }

    // All carousels handle infinite wrapping intelligently
    // Calculate the shortest path to the target
    const startNormalized = ((startIndex % totalItems) + totalItems) % totalItems;
    const targetNormalized = ((targetIndex % totalItems) + totalItems) % totalItems;

    const directDistance = Math.abs(targetNormalized - startNormalized);
    const wrapDistance = totalItems - directDistance;

    // Use shortest path
    if (wrapDistance < directDistance) {
      // Wrapping is shorter - adjust target to wrap in correct direction
      if (targetNormalized > startNormalized) {
        // Target is ahead, so wrap backwards (go negative)
        targetIndex = startIndex - (totalItems - (targetNormalized - startNormalized));
      } else {
        // Target is behind, so wrap forwards (go positive)
        targetIndex = startIndex + (totalItems - (startNormalized - targetNormalized));
      }
    } else {
      // Direct path is shorter - use direct movement
      targetIndex = startIndex + (targetNormalized - startNormalized);
    }

    logGesture('snap-start', {
      requestedTarget: snapTarget,
      chosenTarget: targetIndex,
      startIndex,
      startNormalized,
      targetNormalized,
      directDistance,
      wrapDistance
    });

    if (shouldVerboseLog) {
      console.log('[Carousel][snap] compute-path (once)', {
        startIndex,
        snapTarget,
        startNormalized,
        targetNormalized,
        directDistance,
        wrapDistance,
        chosenTarget: targetIndex
      });
    }

    const startTime = Date.now();
    const duration = 300;
    let lastFrameTime = startTime;
    let frameCount = 0;

    // Cache getItemTransform parameters for direct DOM updates
    snapAnimatedIndexRef.current = startIndex;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Track frame timing to detect drops
      frameCount++;
      const frameDelta = now - lastFrameTime;
      if (shouldVerboseLog && frameCount % 10 === 0) {
        // Log every 10th frame to avoid spam
        const avgFrameTime = elapsed / frameCount;
        if (avgFrameTime > 20) { // Warn if average frame time > 20ms (should be ~16ms for 60fps)
          console.warn('[Carousel][snap] slow frames', { avgFrameTime: avgFrameTime.toFixed(2), frameDelta });
        }
      }
      lastFrameTime = now;

      // Smooth easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const animatedIndex = startIndex + (targetIndex - startIndex) * easeOut;
      snapAnimatedIndexRef.current = animatedIndex;

      // Direct DOM manipulation for smooth animation (bypasses React re-renders)
      // This is critical for Safari performance - no React re-renders during animation
      allItems.forEach((item, index) => {
        const wrapper = slideWrapperRefs.current[index];
        if (!wrapper) return;

        let transform, opacity, zIndex;
        if (allItemsFit) {
          // Static layout - center all items
          const startOffset = -(totalItems - 1) * staticSpacing / 2;
          const x = startOffset + index * staticSpacing;
          transform = `translate3d(${x}px, 0, 0px) scale(1)`;
          opacity = 1;
          zIndex = 90;
        } else {
          // 3D circular carousel - use animatedIndex directly
          const { x, z, scale, opacity: animatedOpacity, zIndex: animatedZIndex } = getCircularTransformState(
            index - animatedIndex,
            totalItems,
            radius
          );
          transform = `translate3d(${x}px, 0, ${z}px) scale(${scale})`;
          opacity = animatedOpacity;
          zIndex = animatedZIndex;
        }

        wrapper.style.transform = transform;
        wrapper.style.opacity = opacity;
        wrapper.style.zIndex = zIndex;
      });

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete - update React state once
        setIsSnapping(false);
        setIsDragging(false);
        // Normalize the final index for wrapping
        const finalIndex = ((targetIndex % totalItems) + totalItems) % totalItems;

        // Force cursor reset by temporarily changing and restoring it
        if (carouselRef.current) {
          carouselRef.current.style.cursor = 'auto';
          setTimeout(() => {
            if (carouselRef.current) {
              carouselRef.current.style.cursor = 'default';
            }
          }, 10);
        }

        // Update React state once at the end (triggers one final render with correct state)
        setCurrentIndex(finalIndex);
        logGesture('snap-complete', {
          chosenTarget: targetIndex,
          finalIndex,
          totalFrames: frameCount,
          totalTime: elapsed
        });
        if (shouldVerboseLog) {
          console.log('[Carousel][snap] complete', { finalIndex, totalFrames: frameCount, totalTime: elapsed });
        }
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [allItems, allItemsFit, dragDisabled, isSnapping, itemCount, logGesture, radius, shouldVerboseLog, snapTarget, staticSpacing]);

  // Event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Keyboard navigation
  useEffect(() => {
    if (shouldSuspendHeavyRender) {
      return undefined;
    }

    const handleKeyDown = (e) => {
      // Prevent keyboard navigation if already snapping (avoid conflicts)
      if (isSnapping) {
        return;
      }

      // Force reset dragging state if keyboard is used (keyboard should never happen during drag)
      if (isDragging) {
        setIsDragging(false);
      }

      const currentRounded = Math.round(currentIndex);
      let newIndex;

      if (e.keyCode === 37) { // Left arrow
        newIndex = currentRounded - 1;

        // Only trigger if different from current
        if (Math.abs(newIndex - currentIndex) > 0.01) {
          setSnapTarget(newIndex);
          setIsSnapping(true);
        }
      }
      if (e.keyCode === 39) { // Right arrow
        newIndex = currentRounded + 1;

        // Only trigger if different from current
        if (Math.abs(newIndex - currentIndex) > 0.01) {
          setSnapTarget(newIndex);
          setIsSnapping(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, allItems.length, isSnapping, isDragging, shouldSuspendHeavyRender]);

  // Check if dragging should be disabled (static layout)
  const shouldDisableDragging = useCallback(() => dragDisabled, [dragDisabled]);

  // Mouse and touch event handlers (non-hook functions)
  const handleMouseDown = (e) => {
    if (shouldDisableDragging()) {
      return; // Disable dragging when items fit
    }
    if (isSnapping || shouldSuspendHeavyRender) {
      return;
    }
    gestureSessionRef.current += 1;
    logGesture('drag-start', {
      pointer: 'mouse',
      startPosition: { x: e.clientX, y: e.clientY },
      whileSnapping: isSnapping
    });
    // Reset tilt when dragging starts
    setHoveredCardIndex(null);
    setMousePosition({ x: 0, y: 0 });
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragOffset(0);
    setLastPosition(e.clientX);
    setLastTime(Date.now());
    setDragStartPosition({ x: e.clientX, y: e.clientY });
    dragStartPositionRef.current = { x: e.clientX, y: e.clientY };
    tapActionHandledRef.current = false;
    setTotalDragDistance(0);
  };

  const handleTouchStart = (e) => {
    if (shouldDisableDragging()) return; // Disable dragging when items fit
    if (isSnapping || shouldSuspendHeavyRender) {
      return;
    }
    gestureSessionRef.current += 1;
    const touch = e.touches[0];
    logGesture('drag-start', {
      pointer: 'touch',
      startPosition: { x: touch.clientX, y: touch.clientY },
      whileSnapping: isSnapping
    });
    gestureDirectionRef.current = null;
    setIsDragging(true);
    const startTime = Date.now();
    setDragStart(touch.clientX);
    setDragOffset(0);
    setLastPosition(touch.clientX);
    setLastTime(startTime);
    setTouchStartTime(startTime);
    setDragStartPosition({ x: touch.clientX, y: touch.clientY });
    dragStartPositionRef.current = { x: touch.clientX, y: touch.clientY };
    tapActionHandledRef.current = false;
    setTotalDragDistance(0);
  };

  const triggerSnapToCard = useCallback((index) => {
    setCurrentIndex(gestureStateRef.current.currentIndex);
    setSnapTarget(index);
    setIsSnapping(true);
    setTotalDragDistance(0);
  }, []);

  const handleSlidePointerUp = useCallback((e, index, hasFlipBack, transform) => {
    if (isSnapping || shouldSuspendHeavyRender) {
      return;
    }

    if (e.target instanceof Element && e.target.closest('.product-slide-back-close')) {
      return;
    }

    const TAP_THRESHOLD = 15;
    const endX = e.clientX ?? e.changedTouches?.[0]?.clientX ?? 0;
    const endY = e.clientY ?? e.changedTouches?.[0]?.clientY ?? 0;
    const start = dragStartPositionRef.current;
    const distance = Math.sqrt((endX - start.x) ** 2 + (endY - start.y) ** 2);
    if (distance > TAP_THRESHOLD) return;

    const shouldSnapToCard = !transform.staticLayout && !transform.isCenter;
    if (shouldSnapToCard) {
      e.preventDefault();
      e.stopPropagation();
      tapActionHandledRef.current = true;
      triggerSnapToCard(index);
      return;
    }

    const isCenterOrAny = navigateOnAnySlide || transform.staticLayout || transform.isCenter;
    if (!onProductClick && hasFlipBack && isCenterOrAny) {
      if (shouldVerboseLog) {
        console.log('[Carousel] Flip toggled (pointerUp)', { index });
      }
      e.preventDefault();
      e.stopPropagation();
      tapActionHandledRef.current = true;
      setFlippedIndex(prev => (prev === index ? null : index));
    }
  }, [isSnapping, navigateOnAnySlide, onProductClick, shouldSuspendHeavyRender, shouldVerboseLog, triggerSnapToCard]);

  if (shouldSuspendHeavyRender) {
    return (
      <div className="carousel-container carousel-container--dormant" aria-hidden="true">
        <div className="performant-carousel-wrapper performant-carousel-wrapper--dormant" />
      </div>
    );
  }

  // 3D carousel for larger collections
  return (
    <div className="carousel-container">
      <div
        ref={carouselRef}
        className="performant-carousel-wrapper"
        style={{
          cursor: isDragging ? 'grabbing' : 'default',
          willChange: shouldPromoteLayers ? 'transform' : 'auto'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Render exiting products with fade-out animation */}
        {exitingProducts.map((product, index) => {
          const baseTransform = getItemTransform(index, -dragOffset);

          return (
            <div
              key={`exiting-${product.id}`}
              className="product-slide-wrapper"
              style={{
                position: 'absolute',
                width: `${itemWidth}px`,
                height: `${itemHeight}px`,
                left: '50%',
                top: '50%',
                marginLeft: `-${itemWidth / 2}px`,
                marginTop: `-${itemHeight / 2}px`,
                transform: `${baseTransform.transform} scale(0.8)`, // Scale down while exiting
                opacity: 0, // Fade out
                zIndex: baseTransform.zIndex - 100, // Behind current items
                transformStyle: 'preserve-3d',
                pointerEvents: 'none',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' // Smooth exit
              }}
              onClick={() => {}} // Disabled for exiting items
            >
              <div className="product-slide">
                <div className="slide-image">
                  {product.images?.[0] ? (
                    <OptimizedImage
                      src={product.images[0]}
                      alt={product.name}
                      loading="lazy"
                      className="product-slide-image"
                      draggable="false"
                    />
                  ) : (
                    <div className="placeholder-image">
                      <ShoppingBag size={48} />
                    </div>
                  )}
                </div>
                <div className="slide-title">
                  {product.name} {product.type}
                </div>
              </div>
            </div>
          );
        })}

        {/* Render current items (collection info card + products) */}
        {allItems.map((item, index) => {
          const transform = getItemTransform(index, -dragOffset);
          const isEntering = animatingProducts.some(p => p.id === item.id);
          // Only calculate tilt for the hovered card to avoid unnecessary calculations
          const isHovered = tiltEnabled && hoveredCardIndex === index && !isDragging;
          const tilt = isHovered ? getTiltTransform(index) : { rotateX: 0, rotateY: 0, scale: 1, lightX: 0, lightY: 0 };
          const baseTransformValue = isEntering
            ? `${transform.transform} scale(0.8) translateY(20px)`
            : transform.transform;
          const allowTilt = !isEntering && !isDragging && isHovered;
          const tiltStyle = allowTilt
            ? {
                transform: `rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) scale(${tilt.scale})`,
                transition: isDragging ? 'none' : 'transform 0.15s ease-out'
              }
            : {
                transform: 'none',
                transition: isDragging ? 'none' : 'transform 0.15s ease-out'
              };

          // Combine carousel transform with tilt transform

          // Check if this is the collection info card
          if (item.isCollectionInfo) {
            return (
              <div
                key={item.id}
                ref={(el) => {
                  if (el) {
                    slideWrapperRefs.current[index] = el;
                    cardRefs.current[index] = el.querySelector('.product-slide') || el;
                  } else {
                    delete slideWrapperRefs.current[index];
                    delete cardRefs.current[index];
                  }
                }}
                className="product-slide-wrapper"
                style={{
                  position: 'absolute',
                  width: `${itemWidth}px`,
                  height: `${itemHeight}px`,
                  left: '50%',
                  top: '50%',
                  marginLeft: `-${itemWidth / 2}px`,
                  marginTop: `-${itemHeight / 2}px`,
                  transform: baseTransformValue,
                opacity: transform.opacity,
                zIndex: transform.zIndex,
                transformStyle: 'preserve-3d',
                willChange: shouldPromoteLayers ? 'transform, opacity' : 'auto',
                pointerEvents: isSnapping ? 'none' : 'auto',
                cursor: 'default',
                transition: isSnapping
                  ? 'none'
                    : (isDragging ? 'none' : 'transform 0.15s ease-out')
                }}
                onMouseEnter={(e) => handleCardMouseEnter(index, e)}
                onMouseMove={(e) => handleCardMouseMove(index, e)}
                onMouseLeave={handleCardMouseLeave}
              >
                <div
                  className={`product-slide collection-info-card ${hoveredCardIndex === index && !isDragging ? 'is-hovered' : ''}`}
                  style={{
                    ...tiltStyle,
                    '--light-x': `${tilt.lightX}%`,
                    '--light-y': `${tilt.lightY}%`
                  }}
                >
                  <div className="slide-image">
                    {item.aboutImageSrc ? (
                      <OptimizedImage
                        src={item.aboutImageSrc}
                        alt={`${item.projectName} collection`}
                        loading="lazy"
                        className="product-slide-image"
                        draggable="false"
                      />
                    ) : (
                      <div className="placeholder-image">
                        <ShoppingBag size={48} />
                      </div>
                    )}
                  </div>

                  {/* Collection intro text overlay */}
                  <div className="collection-info-overlay">
                    <div className="collection-info-title">
                      <span className="collection-info-project">{item.projectName}</span>
                      {item.season ? <span className="collection-info-season"> / {item.season}</span> : null}
                    </div>
                    {item.introText ? (
                      <div className="collection-info-body-wrapper">
                        <p className="collection-info-body">{item.introText}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          }

          // Regular product slide
          const hasGarmentData = item.garmentData && item.garmentData.length > 0;
          const hasFlipBack = hasGarmentData || (item.garmentSlugs && item.garmentSlugs.length > 0);
          const isFlipped = flippedIndex === index;
          const isCenterOrAny = navigateOnAnySlide || transform.staticLayout || transform.isCenter;
          const canAdvanceToCard = !transform.staticLayout && !transform.isCenter;
          const formatPrice = (p) => (p != null ? `EUR ${Number(p).toLocaleString('de-DE')}` : '');
          const displayTitle = formatItemDisplayTitle(item);
          const backFaceContent = hasFlipBack && (
            hasGarmentData ? (
              <>
                <button
                  type="button"
                  className="product-slide-back-close"
                  aria-label="Close garment details"
                  onPointerUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFlippedIndex(null);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFlippedIndex(null);
                  }}
                >
                  <X size={14} strokeWidth={1.75} />
                </button>
                <div className="product-slide-back-content">
                  {item.garmentData.map((garment, garmentIndex) => (
                    <div
                      key={`${garment.name}-${garment.type}-${garmentIndex}`}
                      className="product-slide-back-block"
                    >
                      <div className="product-slide-back-line product-slide-back-line-primary">
                        {formatGarmentLabel(garment)}
                      </div>
                      {formatMaterialsLine(garment.materials) ? (
                        <div className="product-slide-back-line product-slide-back-line-secondary">
                          {formatMaterialsLine(garment.materials)}
                        </div>
                      ) : null}
                      {garment.price != null ? (
                        <div className="product-slide-back-line product-slide-back-line-secondary">
                          {formatPrice(garment.price)}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="product-slide-back-close"
                  aria-label="Close garment details"
                  onPointerUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFlippedIndex(null);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFlippedIndex(null);
                  }}
                >
                  <X size={14} strokeWidth={1.75} />
                </button>
                <div className="product-slide-back-content">
                  <div className="product-slide-back-block">
                    <div className="product-slide-back-line product-slide-back-line-primary">{displayTitle}</div>
                    <div className="product-slide-back-line product-slide-back-line-secondary">
                      Garment details are not synced yet.
                    </div>
                  </div>
                </div>
              </>
            )
          );

          return (
            <div
              key={item.id || `slide-${index}`}
              ref={(el) => {
                if (el) {
                  // Store wrapper ref for direct DOM manipulation during snap animation
                  slideWrapperRefs.current[index] = el;
                  // Also store in cardRefs for tilt calculations
                  cardRefs.current[index] = el.querySelector('.product-slide') || el;
                } else {
                  delete slideWrapperRefs.current[index];
                  delete cardRefs.current[index];
                }
              }}
              className="product-slide-wrapper"
              style={{
                position: 'absolute',
                width: `${itemWidth}px`,
                height: `${itemHeight}px`,
                left: '50%',
                top: '50%',
                marginLeft: `-${itemWidth / 2}px`,
                marginTop: `-${itemHeight / 2}px`,
                transform: baseTransformValue,
                opacity: isEntering ? 0 : transform.opacity,
                zIndex: transform.zIndex,
                transformStyle: 'preserve-3d',
                willChange: shouldPromoteLayers ? 'transform, opacity' : 'auto',
                pointerEvents: isSnapping ? 'none' : 'auto',
                cursor: canAdvanceToCard || (isCenterOrAny && (onProductClick || hasFlipBack)) ? 'pointer' : 'default',
                transition: isSnapping
                  ? 'none' // Disable transition during snap (we use direct DOM manipulation)
                  : (isEntering
                    ? 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    : (isDragging ? 'none' : 'transform 0.15s ease-out')) // Smooth tilt transition
              }}
              onMouseEnter={(e) => handleCardMouseEnter(index, e)}
              onMouseMove={(e) => handleCardMouseMove(index, e)}
              onMouseLeave={handleCardMouseLeave}
              onMouseDown={(e) => {
                // Ensure drag events propagate to carousel wrapper
                // Don't prevent default - let it bubble to wrapper's handleMouseDown
              }}
              onPointerUpCapture={(e) => {
                handleSlidePointerUp(e, index, hasFlipBack, transform);
              }}
              onClick={() => {
                if (isSnapping) {
                  return;
                }

                // Check if this was a drag gesture (moved more than 10px)
                const DRAG_THRESHOLD = 10;
                if (totalDragDistance > DRAG_THRESHOLD) {
                  if (shouldVerboseLog && hasFlipBack) {
                    console.log('[Carousel] Click ignored (drag)', { totalDragDistance, index });
                  }
                  setTotalDragDistance(0);
                  return;
                }

                const shouldSnapToCard = !transform.staticLayout && !transform.isCenter;

                if (shouldSnapToCard) {
                  if (!tapActionHandledRef.current) {
                    triggerSnapToCard(index);
                  }
                  tapActionHandledRef.current = false;
                  setTotalDragDistance(0);
                  return;
                }

                // Flip is handled in onPointerUp for reliable tap detection (mouse + touch)
                if (onProductClick && isCenterOrAny) {
                  onProductClick(item);
                }

                // Reset drag distance after handling click
                tapActionHandledRef.current = false;
                setTotalDragDistance(0);
              }}
            >
              {hasFlipBack ? (
                <div
                  className={`product-slide product-slide-flip ${isFlipped ? 'is-flipped' : ''} ${hoveredCardIndex === index && !isDragging ? 'is-hovered' : ''}`}
                  style={{
                    ...tiltStyle,
                    '--light-x': `${tilt.lightX}%`,
                    '--light-y': `${tilt.lightY}%`
                  }}
                >
                  <div className="product-slide-inner">
                    <div className="product-slide-front">
                      <div className="slide-image">
                        {item.images?.[0] ? (
                          <OptimizedImage
                            src={item.images[0]}
                            alt={item.name}
                            loading="lazy"
                            className="product-slide-image"
                            draggable="false"
                          />
                        ) : (
                          <div className="placeholder-image">
                            <ShoppingBag size={48} />
                          </div>
                        )}
                      </div>
                      <div className="slide-title">{displayTitle}</div>
                    </div>
                    <div className="product-slide-back">
                      {backFaceContent}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`product-slide ${hoveredCardIndex === index && !isDragging ? 'is-hovered' : ''}`}
                  style={{
                    ...tiltStyle,
                    '--light-x': `${tilt.lightX}%`,
                    '--light-y': `${tilt.lightY}%`
                  }}
                >
                  <div className="slide-image">
                    {item.images?.[0] ? (
                      <OptimizedImage
                        src={item.images[0]}
                        alt={item.name}
                        loading="lazy"
                        className="product-slide-image"
                        draggable="false"
                      />
                    ) : (
                      <div className="placeholder-image">
                        <ShoppingBag size={48} />
                      </div>
                    )}
                  </div>
                  <div className="slide-title">{displayTitle}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomCarousel;
